import {
    Button,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Collapse,
    Divider,
    makeStyles,
    Typography,
} from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProfile } from '../../data';

const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 520,
        margin: '0 auto',
        padding: theme.spacing(3, 0, 4),
        textAlign: 'left',
    },
    pageTitle: {
        marginBottom: theme.spacing(3),
    },
    sectionLabel: {
        margin: theme.spacing(1.5, 0, 1),
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
    },
    divider: {
        margin: theme.spacing(2, 0, 2.5),
    },
    timelineGroup: {
        display: 'grid',
        gridTemplateColumns: '58px minmax(0, 1fr)',
        columnGap: theme.spacing(2),
    },
    timelineRail: {
        position: 'relative',
        textAlign: 'right',
        paddingTop: theme.spacing(0.5),
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 30,
            bottom: -12,
            right: -13,
            width: 2,
            backgroundColor: theme.palette.divider,
        },
    },
    finalRail: {
        '&::after': {
            display: 'none',
        },
    },
    year: {
        fontWeight: 700,
        color: theme.palette.text.primary,
    },
    dot: {
        position: 'absolute',
        top: 22,
        right: -18,
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: theme.palette.grey[500],
        border: `3px solid ${theme.palette.background.default}`,
        boxSizing: 'content-box',
        zIndex: 1,
    },
    activeDot: {
        backgroundColor: theme.palette.secondary.main,
    },
    groupContent: {
        paddingBottom: theme.spacing(3),
    },
    tournamentLabel: {
        display: 'block',
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    leagueCard: {
        marginBottom: theme.spacing(1.5),
    },
    activeCard: {
        borderLeft: `4px solid ${theme.palette.secondary.main}`,
    },
    cardAction: {
        display: 'block',
        color: 'inherit',
        textDecoration: 'none',
        '&:visited': {
            color: 'inherit',
        },
    },
    cardRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    cardDetails: {
        minWidth: 0,
    },
    status: {
        marginTop: theme.spacing(0.5),
        color: theme.palette.text.secondary,
    },
    winner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.25),
        color: theme.palette.text.primary,
    },
    trophy: {
        color: '#c58b00',
    },
    winnerScore: {
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    archiveToggle: {
        gridColumn: '2',
        width: '100%',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
    },
    expandIcon: {
        transition: theme.transitions.create('transform'),
    },
    expandIconOpen: {
        transform: 'rotate(180deg)',
    },
    createButton: {
        gridColumn: '2',
        marginTop: theme.spacing(1),
    },
    historySection: {
        marginTop: theme.spacing(1),
    },
    emptyState: {
        color: theme.palette.text.secondary,
        padding: theme.spacing(2, 0),
    },
    actionsRow: {
        borderTop: `1px solid ${theme.palette.divider}`,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    teamActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(0.5),
    },
    teamLabel: {
        minWidth: 60,
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    actionBtn: {
        textTransform: 'none',
        fontSize: '0.75rem',
        padding: theme.spacing(0.25, 1),
    },
}));

const newestFirst = (a, b) =>
    (b.tournamentSortOrder || 0) - (a.tournamentSortOrder || 0)
    || a.name.localeCompare(b.name);

const isVisibleLeague = (league) => {
    const name = (league.name || '').trim();
    return !/^test(\b|[-_ ]|$)/i.test(name);
};

const groupByTournament = (leagues) => leagues.reduce((groups, league) => {
    const key = league.tournament;
    if (!groups[key]) {
        groups[key] = {
            tournament: key,
            label: league.tournamentLabel || key,
            year: league.tournamentYear,
            sortOrder: league.tournamentSortOrder || 0,
            leagues: [],
        };
    }
    groups[key].leagues.push(league);
    return groups;
}, {});

function LeagueCard({ league, active, classes }) {
    const winners = league.winners || [];
    const topper = league.topper || null;
    const winnerNames = winners.map(winner => winner.name).join(' & ');
    const winningScore = winners.length > 0 ? winners[0].score : undefined;
    const topperName = topper ? topper.name : undefined;
    const topperScore = topper ? topper.score : undefined;
    const myTeams = league.myTeams || [];
    const isAdmin = league.isAdmin || false;
    const draftType = league.draftType || null;
    const status = league.status || 'predraft';

    return (
        <Card className={`${classes.leagueCard} ${active ? classes.activeCard : ''}`}>
            <CardActionArea
                component={Link}
                to={`/leagues/${league.leagueID}`}
                className={classes.cardAction}
            >
                <CardContent className={classes.cardRow}>
                    <div className={classes.cardDetails}>
                        <Typography variant="h6">{league.name}</Typography>
                        {active && topper && (
                            <div className={classes.winner}>
                                <EmojiEventsIcon className={classes.trophy} aria-hidden="true" />
                                <div>
                                    <Typography component="div">
                                        Topper: {topperName}
                                    </Typography>
                                    <Typography variant="body2" className={classes.winnerScore}>
                                        {Number(topperScore).toLocaleString()} pts
                                    </Typography>
                                </div>
                            </div>
                        )}
                        {active && !topper && <Typography className={classes.status}>In progress</Typography>}
                        {!active && winners.length > 0 && (
                            <div className={classes.winner}>
                                <EmojiEventsIcon className={classes.trophy} aria-hidden="true" />
                                <div>
                                    <Typography component="div">
                                        {winners.length > 1 ? 'Joint winners: ' : 'Winners: '}{winnerNames}
                                    </Typography>
                                    <Typography variant="body2" className={classes.winnerScore}>
                                        {Number(winningScore).toLocaleString()} pts
                                    </Typography>
                                </div>
                            </div>
                        )}
                        {!active && winners.length === 0 && (
                            <Typography className={classes.status}>Final result unavailable</Typography>
                        )}
                    </div>
                    <ChevronRightIcon aria-hidden="true" />
                </CardContent>
            </CardActionArea>
            {active && (myTeams.length > 0 || isAdmin || (draftType === 'snake' && (status === 'predraft' || status === 'drafting'))) && (
                <CardContent className={classes.actionsRow}>
                    {myTeams.map(teamName => (
                        <div key={teamName} className={classes.teamActions}>
                            <Typography variant="caption" className={classes.teamLabel}>{teamName}</Typography>
                            <Button component={Link} to={`/leagues/${league.leagueID}/teams/${teamName}`}
                                size="small" variant="outlined" className={classes.actionBtn}>
                                Set XI
                            </Button>
                            <Button component={Link} to={`/leagues/${league.leagueID}/teams/${teamName}/captains`}
                                size="small" variant="outlined" className={classes.actionBtn}>
                                Captains
                            </Button>
                        </div>
                    ))}
                    {isAdmin && (league.waivers || draftType === 'auction') && (
                        <div className={classes.teamActions}>
                            <Typography variant="caption" className={classes.teamLabel}>Admin</Typography>
                            {league.waivers && (
                                <Button component={Link} to={`/leagues/${league.leagueID}/admin`}
                                    size="small" variant="outlined" color="secondary" className={classes.actionBtn}>
                                    Confirm Subs
                                </Button>
                            )}
                            {draftType === 'auction' && (
                                <Button component={Link} to={`/leagues/${league.leagueID}/admin`}
                                    size="small" variant="outlined" color="secondary" className={classes.actionBtn}>
                                    Confirm XI
                                </Button>
                            )}
                        </div>
                    )}
                    {isAdmin && league.draftType === 'snake' && league.status === 'predraft' && (
                        <div className={classes.teamActions}>
                            <Typography variant="caption" className={classes.teamLabel}>Draft</Typography>
                            <Button component={Link} to={`/leagues/${league.leagueID}/snake-draft/order`}
                                size="small" variant="contained" color="primary" className={classes.actionBtn}>
                                Set Draft Order
                            </Button>
                        </div>
                    )}
                    {league.draftType === 'snake' && league.status === 'drafting' && (
                        <div className={classes.teamActions}>
                            <Typography variant="caption" className={classes.teamLabel}>Draft</Typography>
                            <Button component={Link} to={`/leagues/${league.leagueID}/snake-draft`}
                                size="small" variant="contained" color="primary" className={classes.actionBtn}>
                                {league.snakeNextPick
                                    ? `Next: ${league.snakeNextPick}`
                                    : 'View Draft'
                                }
                            </Button>
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    );
}

function TimelineGroup({ group, active, final, classes }) {
    return (
        <div className={classes.timelineGroup}>
            <div className={`${classes.timelineRail} ${final ? classes.finalRail : ''}`}>
                <Typography className={classes.year}>{group.year || '—'}</Typography>
                <span className={`${classes.dot} ${active ? classes.activeDot : ''}`} />
            </div>
            <div className={classes.groupContent}>
                <Typography variant="caption" className={classes.tournamentLabel}>
                    {group.label}
                </Typography>
                {group.leagues.map(league => (
                    <LeagueCard key={league.leagueID} league={league} active={active} classes={classes} />
                ))}
            </div>
        </div>
    );
}

export default function HomepageUser() {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [leagues, setLeagues] = useState([]);
    const [showOlder, setShowOlder] = useState(false);

    useEffect(() => {
        (async () => {
            const profile = await getProfile();
            setLeagues((profile.leagues || []).filter(isVisibleLeague).slice().sort(newestFirst));
            setLoading(false);
        })();
    }, []);

    const activeGroups = Object.values(groupByTournament(leagues.filter(league => league.isActive)))
        .sort((a, b) => b.sortOrder - a.sortOrder);
    const archivedGroups = Object.values(groupByTournament(leagues.filter(league => !league.isActive)))
        .sort((a, b) => b.sortOrder - a.sortOrder);
    const recentArchived = archivedGroups.slice(0, 1);
    const olderArchived = archivedGroups.slice(1);

    return (
        <main className={classes.root}>
            <Typography variant="h5" className={classes.pageTitle}>Active Leagues</Typography>
            {loading && <CircularProgress aria-label="Loading leagues" />}
            {!loading && leagues.length === 0 && (
                <Typography className={classes.emptyState}>No leagues yet</Typography>
            )}
            {!loading && activeGroups.length > 0 && (
                <>
                    <Typography variant="overline" className={classes.sectionLabel}>Active leagues</Typography>
                    {activeGroups.map((group, index) => (
                        <TimelineGroup
                            key={group.tournament}
                            group={group}
                            active
                            final={index === activeGroups.length - 1}
                            classes={classes}
                        />
                    ))}
                </>
            )}
            {!loading && archivedGroups.length > 0 && (
                <>
                    {(activeGroups.length > 0 || archivedGroups.length > 0) && (
                        <Divider className={classes.divider} />
                    )}
                    <Typography variant="overline" className={classes.sectionLabel}>Tournament history</Typography>
                    <div className={classes.historySection}>
                        {recentArchived.map((group, index) => (
                            <TimelineGroup
                                key={group.tournament}
                                group={group}
                                final={olderArchived.length === 0 && index === recentArchived.length - 1}
                                classes={classes}
                            />
                        ))}
                    </div>
                    {olderArchived.length > 0 && (
                        <>
                            <Button
                                variant="outlined"
                                className={classes.archiveToggle}
                                onClick={() => setShowOlder(open => !open)}
                                aria-expanded={showOlder}
                                endIcon={
                                    <ExpandMoreIcon
                                        className={`${classes.expandIcon} ${showOlder ? classes.expandIconOpen : ''}`}
                                    />
                                }
                            >
                                {showOlder ? 'Hide earlier years' : `Show earlier years (${olderArchived.length})`}
                            </Button>
                            <Collapse in={showOlder} unmountOnExit>
                                {olderArchived.map((group, index) => (
                                    <TimelineGroup
                                        key={group.tournament}
                                        group={group}
                                        final={index === olderArchived.length - 1}
                                        classes={classes}
                                    />
                                ))}
                            </Collapse>
                        </>
                    )}
                </>
            )}
            {!loading && (
                <Button
                    component={Link}
                    to="/leagues/create"
                    variant="outlined"
                    color="primary"
                    className={classes.createButton}
                    startIcon={<AddCircleOutlineIcon />}
                >
                    Create a league
                </Button>
            )}
        </main>
    );
}

export { groupByTournament, newestFirst };
